<script setup lang="ts">
  import { IBranch } from '@/interfaces/IBranch';
  import { vueData } from '@/VueData';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { computed } from 'vue';

  // Store
  const restaurantStore = useRestaurantStore();

  // Computed - use branches from store for reactivity
  const branches = computed(() => restaurantStore.visibleBranches || []);

  // Methods
  const selectBranch = (branch: IBranch) => {
    vueData.setCurrentBranch(branch);
  };
</script>

<template>
  <v-card class="branch-selector-card" elevation="0">
    <v-card-text class="selector-content">
      <div class="selector-label">
        <v-icon color="orange" class="mr-2">mdi-store-outline</v-icon>
        <span class="label-text">{{ $t('dashboard.branch') }}</span>
      </div>

      <v-select
        :items="branches"
        :value="restaurantStore.selectedBranch"
        item-text="name"
        return-object
        @change="selectBranch"
        class="branch-select"
        hide-details
        dense
        outlined
      >
        <template v-slot:selection="{ item }">
          <div class="selected-branch">
            <span class="branch-name">{{ item.name }}</span>
            <v-chip
              v-if="!item.active"
              x-small
              color="error"
              text-color="white"
              class="ml-2"
            >
              {{ $t('dashboard.branchInactive') }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item="{ item }">
          <div class="branch-item">
            <span class="branch-name">{{ item.name }}</span>
            <v-chip
              v-if="!item.active"
              x-small
              color="error"
              text-color="white"
              class="ml-auto"
            >
              {{ $t('dashboard.branchInactive') }}
            </v-chip>
          </div>
        </template>
      </v-select>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .branch-selector-card {
    @apply tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg;

    .selector-content {
      @apply tw-flex tw-items-center tw-space-x-4 tw-py-3;
    }

    .selector-label {
      @apply tw-flex tw-items-center tw-text-orange-800 tw-font-medium;

      .label-text {
        @apply tw-text-sm;
      }
    }

    .branch-select {
      @apply tw-max-w-xs;

      ::v-deep .v-input__control {
        @apply tw-bg-white;
      }
    }

    .selected-branch,
    .branch-item {
      @apply tw-flex tw-items-center tw-w-full;

      .branch-name {
        @apply tw-font-medium;
      }
    }
  }

  // Responsive adjustments
  @media (max-width: 640px) {
    .branch-selector-card {
      .selector-content {
        @apply tw-flex-col tw-items-start tw-space-x-0 tw-space-y-2;
      }

      .branch-select {
        @apply tw-w-full tw-max-w-none;
      }
    }
  }
</style>
